import React, { useEffect, useState } from 'react';
import { Tooltip } from '@mantine/core';
import Button from './components/Button';
import DetailSection from './components/DetailSection';
import Countdown from 'react-countdown';
import Gallery from './components/nft/Gallery';
import MintCountDown from './components/nft/MintCountDown';

import { useGeoBlocking } from '../hooks/useGeoBlocking';
import { useOwnedKiosk } from '../hooks/kiosk';
import { executeApiCall } from './utils';
import { useEssential } from './App';
import { normalizeSuiAddress } from '@mysten/sui.js/utils';
import { SUDO_MINT_TICKET_TYPE_SUFFIX } from '../utils/utils';
import { useCardTrackers } from '../hooks/useCardTrackers';
import { useKioskClient } from '../context/KioskClientContext';
import { useSuiClient } from '@mysten/dapp-kit';

const stages = [
  {
    id: 'Black',
    name: 'Black Card',
    version: '"S" version',
    amount: '20',
    mintingMethod: 'Mint Ticket',
    startAt: new Date('2024-12-16T00:00:00Z'),
    publicMintAt: new Date('2025-12-16T00:00:00Z'), // Placeholder: Not going to happen
    isEnd: false,
    price: 10,
    tier: 0,
  },
  {
    id: 'Emerald',
    name: 'Emerald Card',
    version: '"U" version',
    amount: '180',
    mintingMethod: 'Mint Ticket',
    startAt: new Date('2024-12-16T00:00:00Z'),
    publicMintAt: new Date('2025-12-16T00:00:00Z'), // Placeholder: Not going to happen
    isEnd: false,
    price: 10,
    tier: 1,
  },
  {
    id: 'Silver',
    name: 'Silver Card',
    version: '"D" version',
    amount: '200',
    mintingMethod: 'Mint Ticket',
    startAt: new Date('2024-12-16T00:00:00Z'),
    publicMintAt: new Date('2025-12-16T00:00:00Z'), // Placeholder: Not going to happen
    isEnd: false,
    price: 10,
    tier: 2,
  },
  {
    id: 'Silver Public',
    name: 'Silver Card Public Mint',
    version: '"O" version',
    amount: '266',
    mintingMethod: 'Public mint',
    startAt: new Date('2024-12-16T01:00:00Z'),
    publicMintAt: new Date('2024-12-16T01:00:00Z'),
    isEnd: false,
    price: 15,
    tier: 2,
  },
];

const NFT: React.FC = props => {
  const suiClient = useSuiClient();
  const {
    wallet,
    network,
    consts,
    accountData,
    refreshAccount,
    priceLoading,
    walletStatus,
    ownedSudoObjects,
    refetchOwnedObjects,
  } = useEssential();
  const { data: walletKiosk, refetch: refetchOwnedKiosk } = useOwnedKiosk(
    accountData.address,
  );
  const { data: cardTrackers, refetch: refetchCardTrackers } = useCardTrackers({
    consts,
  });

  const [invalid, setInvalid] = useState('');
  const [activeStage, setActiveStage] = useState(stages[0]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const isStart = new Date(activeStage.startAt) <= new Date();
  const isPublicMint = new Date(activeStage.publicMintAt) <= new Date();
  const isBlocked = useGeoBlocking();
  const kioskClient = useKioskClient();

  const getMintTicket = () => {
    if (!ownedSudoObjects) {
      return undefined;
    }

    const ownedMintTickets = ownedSudoObjects?.filter(
      object =>
        object.type ===
        `${consts.sudoNft.package}::${SUDO_MINT_TICKET_TYPE_SUFFIX}`,
    );

    return ownedMintTickets.find(
      mintTicket => mintTicket.display.tier === activeStage.tier.toString(),
    );
  };

  const hasMintTicketForTier = (tier: string) => {
    if (!ownedSudoObjects) {
      return undefined;
    }

    const ownedMintTickets = ownedSudoObjects?.filter(
      object =>
        object.type ===
        `${consts.sudoNft.package}::${SUDO_MINT_TICKET_TYPE_SUFFIX}`,
    );

    return ownedMintTickets.find(
      mintTicket => mintTicket.display.tier === tier,
    );
  };

  useEffect(() => {
    if (isBlocked === null || priceLoading) {
      setInvalid('Loading');
      return;
    }

    if (!accountData.address) {
      setInvalid('Please connect wallet');
      return;
    }

    if (isStart) {
      refetchCardTrackers();
      if (cardTrackers && cardTrackers.cardTrackers) {
        if (
          (cardTrackers.cardTrackers[activeStage.tier] as any).current_count ===
          (cardTrackers.cardTrackers[activeStage.tier] as any).max_count
        ) {
          setInvalid('SOLD OUT');
          return;
        }
      }

      const hasOwnedKiosk = walletKiosk?.caps?.find(
        x =>
          walletKiosk.kioskId &&
          normalizeSuiAddress(x.kioskId).endsWith(walletKiosk.kioskId),
      );
      if ((activeStage.tier === 2 && isPublicMint) || !hasOwnedKiosk) {
        setInvalid('');
        return;
      }

      const ownedMintTickets = ownedSudoObjects?.filter(
        object =>
          object.type ===
          `${consts.sudoNft.package}::${SUDO_MINT_TICKET_TYPE_SUFFIX}`,
      );

      if (!ownedMintTickets) {
        setInvalid('No mint ticket found');
        return;
      }

      const mintTicket = ownedMintTickets.find(
        mintTicket => mintTicket.display.tier === activeStage.tier.toString(),
      );

      if (!mintTicket) {
        setInvalid('No mint ticket found');
        return;
      }
    }

    setInvalid('');
  }, [
    isBlocked,
    priceLoading,
    accountData.address,
    activeStage.tier,
    activeStage.id,
    isStart,
    ownedSudoObjects,
    isPublicMint,
    walletKiosk,
    consts.sudoNft.package,
    cardTrackers,
    refetchCardTrackers,
  ]);

  const isOwnedKiosk = (kioskId: string | undefined) => {
    return walletKiosk?.caps?.find(
      x => kioskId && normalizeSuiAddress(x.kioskId).endsWith(kioskId),
    );
  };

  const clearStates = () => {};

  const hasOwnedKiosk = () => {
    return walletKiosk && isOwnedKiosk(walletKiosk.kioskId);
  };

  const createKiosk = () => {
    if (invalid) {
      return;
    }
    setIsSubmitLoading(true);
    executeApiCall(
      async api => {
        return api.createKiosk(accountData.address, kioskClient);
      },
      res => {
        clearStates();
        const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount, refetchOwnedKiosk, refetchCardTrackers],
    ).finally(() => setIsSubmitLoading(false));
  };

  const mint = () => {
    if (invalid) {
      return;
    }
    setIsSubmitLoading(true);
    executeApiCall(
      async api => {
        let cardTracker = consts.sudoNft.silverCardTracker;
        switch (activeStage.tier) {
          case 2:
            cardTracker = consts.sudoNft.silverCardTracker;
            break;
          case 1:
            cardTracker = consts.sudoNft.emeraldCardTracker;
            break;
          case 0:
            cardTracker = consts.sudoNft.blackCardTracker;
            break;
          default:
            throw Error('Invalid mint tier');
        }

        if (
          !walletKiosk?.kioskId ||
          !walletKiosk?.kioskCap ||
          !isOwnedKiosk(walletKiosk?.kioskId)
        ) {
          throw Error('Invalid kiosk');
        }

        const kiosk = walletKiosk.kioskId;
        const kioskOwnerCap = walletKiosk.kioskCap;

        if (activeStage.tier === 2 && isPublicMint) {
          // Public mint for silver card does not require mint ticket
          return api.publicMintSilverCard(
            'sui',
            activeStage.price * 10 ** consts.coins['sui'].decimals,
            kiosk,
            kioskOwnerCap,
            `${consts.sudoNft.transferPolicy}`,
            cardTracker,
            `${consts.sudoNft.cardRegistry}`,
            `${consts.sudoNft.mintSettings}`,
          );
        }

        const mintTicket = getMintTicket();
        if (!mintTicket) {
          throw Error('Invalid mint ticket');
        }

        return api.mintWithMintTicket(
          'sui',
          activeStage.price * 10 ** consts.coins['sui'].decimals,
          mintTicket.objectId,
          kiosk,
          kioskOwnerCap,
          `${consts.sudoNft.transferPolicy}`,
          cardTracker,
          `${consts.sudoNft.cardRegistry}`,
          `${consts.sudoNft.mintSettings}`,
        );
      },
      res => {
        clearStates();
        const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount, refetchOwnedObjects, refetchCardTrackers],
    ).finally(() => setIsSubmitLoading(false));
  };

  const mintDetail = () => {
    if (activeStage.isEnd) {
      return (
        <div className="flex">
          <div>
            <p>
              <span className="text-md md:text-[24px] font-bold">
                {activeStage.name} is Closed
              </span>
            </p>
          </div>
        </div>
      );
    }
    if (hasOwnedKiosk()) {
      return (
        <div className="flex">
          <div>
            <p className="text-gray1/95 text-xs md:text-sm font-600">Price</p>
            <p>
              <span className="text-md md:text-[24px] font-bold">
                {activeStage.price} SUI
              </span>
            </p>
          </div>
        </div>
      );
    } else {
      if (!accountData.address) {
        return <div style={{ display: 'none' }}></div>;
      } else {
        return (
          <div className="flex">
            <div>
              <p className="text-md md:text-[24px] font-bold">Kiosk</p>
              <p>
                <span className="text-gray1/95 text-xs md:text-sm font-600">
                  Create a kiosk to store your S Card.
                </span>
              </p>
            </div>
          </div>
        );
      }
    }
  };

  const mintButton = () => {
    if (activeStage.isEnd) {
      return (
        <div className="light">
          <Button
            buttonStyle="disabled"
            text="Unavailable"
            onClick={mint}
            isLoading={isSubmitLoading}
            className="app-mint-action-button"
          />
        </div>
      );
    }

    if (hasOwnedKiosk()) {
      return (
        <div className="light">
          <Button
            buttonStyle={invalid ? 'disabled' : 'main'}
            text={invalid ? invalid : 'Mint S Card'}
            onClick={mint}
            isLoading={isSubmitLoading}
            className="app-mint-action-button"
          />
        </div>
      );
    } else {
      return (
        <div className="light">
          <Button
            buttonStyle={invalid ? 'disabled' : 'main'}
            text={invalid ? invalid : 'Create a kiosk'}
            onClick={createKiosk}
            isLoading={isSubmitLoading}
            className="app-mint-action-button"
          />
        </div>
      );
    }
  };

  return (
    <div className={`section-container py-5 sm:py-10 sm:px-14 px-5 relative`}>
      <div className="text-3xl !mb-5">Join the "S"</div>
      <div className="grid grid-cols-1  sm:grid-cols-2 gap-10 w-full">
        <div className="">
          <Gallery></Gallery>
          <div className="text-md leading-normal mb-5 last:mb-0 !mt-6">
            Welcome "Su"!
          </div>
          <div className="text-md leading-normal mb-5 last:mb-0 !mt-6">
            Are you ready for the superior privileges offered by the "S" Card?
          </div>
          <div className="text-md leading-normal mb-5 last:mb-0 !mt-6">
            Now that we are here, it's only one step away for you to become a
            part of "S" community.
          </div>
          <div className="text-md leading-normal mb-5 last:mb-0 !mt-6">
            Be the alpha. Beat the Market. Get the "S" card. Find the real "Su".
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="bg-primary/50 border-[1px] border-secondary rounded-lg !p-4">
            <div className="!mb-4">
              <div className="my-[7px] font-bold">Mint Stages</div>
              <div className="flex flex-col gap-4 !mt-4">
                {stages.map(stage => {
                  return (
                    <Tooltip
                      key={stage.id}
                      color="dark"
                      position="top-start"
                      label={
                        <div className="p-2">
                          <div className="text-base font-bold !mb-2">
                            {stage.name}
                          </div>
                          <div>Amount: {stage.amount}</div>
                          <div>Minting method: {stage.mintingMethod}</div>
                          <div>
                            {' '}
                            Eligibility:{' '}
                            {stage.name === 'Silver Card Public Mint'
                              ? 'Public'
                              : hasMintTicketForTier(stage.tier.toString())
                                ? 'Mint Ticket Detected'
                                : 'N/A'}{' '}
                          </div>
                        </div>
                      }
                    >
                      <div
                        onClick={() => {
                          setActiveStage(stage);
                        }}
                        className={`rounded-xl border-[1px] border-solid ${stage.isEnd ? '' : 'cursor-pointer'} ${activeStage.id === stage.id ? 'border-green' : 'border-secondary'}  !p-3 flex flex-col gap-6 `}
                      >
                        <div className="flex justify-between items-center text-sm">
                          <div className="hidden sm:block border rounded-full !py-1 !px-2 text-[12px] h-fit bg-secondary text-white">
                            {stage.name}{' '}
                            {hasMintTicketForTier(stage.tier.toString())
                              ? '🎟️'
                              : null}
                          </div>
                          <div className="sm:hidden border rounded-full !py-1 !px-2 text-[12px] h-fit bg-secondary text-white">
                            {stage.id}{' '}
                            {hasMintTicketForTier(stage.tier.toString())
                              ? '🎟️'
                              : null}
                          </div>
                          <div className="flex flex-row gap-2 tracking-wide items-center text-center flex-wrap justify-center text-xs sm:text-sm font-medium uppercase">
                            {stage.isEnd ? (
                              <div> Mint Closed </div>
                            ) : (
                              <Countdown
                                date={stage.startAt}
                                renderer={MintCountDown}
                              ></Countdown>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm tracking-wide">
                          <span>{stage.version.toUpperCase()}</span>
                          <b>•</b>
                          <span>MAX {stage.amount}</span>
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
            {isStart ? (
              <div className="flex flex-col !gap-5 !p-4 bg-secondary/50 rounded-lg">
                <div className="flex items-center gap-5 text-[13px]">
                  {isStart && !activeStage.isEnd ? (
                    <div className="flex-grow-0">
                      <div className="!px-3  bg-opacity-10 rounded text-sm whitespace-nowrap min-w-[42px] min-h-[40px] !py-2.5 bg-primary/30">
                        <div className="flex items-center gap-x-2">
                          <span
                            className="flex-shrink-0 !h-2 !w-2 relative flex items-center justify-center"
                            aria-hidden="true"
                          >
                            <span className="absolute !h-2.5 !w-2.5 rounded-full bg-green animate-ping [animation-duration:2s]"></span>
                            <span className="relative block !w-2 !h-2 bg-green rounded-full"></span>
                          </span>
                          <span className="capitalize text-green">live</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'none' }}></div>
                  )}
                </div>
                {mintDetail()}
                {mintButton()}
              </div>
            ) : (
              <div style={{ display: 'none' }}></div>
            )}
          </div>
          <div
            className={
              'bg-primary/50 border-[1px] border-secondary rounded-lg !p-4 flex items-center justify-between'
            }
          >
            <div className="app-mint-overview-card-title flex row justify-start align-center">
              <div>Sudo Ranking</div>
            </div>
            <DetailSection
              title="Your Ranking"
              content={
                !walletStatus.traderRanking
                  ? '0'
                  : walletStatus.traderRanking.toString()
              }
              active={false}
            />
            <DetailSection
              title="Your SPs"
              content={
                !walletStatus.traderPoints
                  ? '0'
                  : walletStatus.traderPoints.toString()
              }
              active={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NFT;
